import { isSinchActionMenuOption } from '../action-menu-option/utils';
import { attrValueToPixels, defineCustomElement, getBooleanAttribute, getIntegerAttribute, NectaryElement, subscribeContext, updateBooleanAttribute, updateIntegerAttribute } from '../utils';
const templateHTML = '<style>:host{display:block;outline:0}#listbox{overflow-y:auto}</style><div id="listbox" role="presentation"><slot></slot></div>';
const ITEM_HEIGHT = 40;
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-action-menu', class extends NectaryElement {
  #$optionSlot;
  #$listbox;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$optionSlot = shadowRoot.querySelector('slot');
    this.#$listbox = shadowRoot.querySelector('#listbox');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    this.setAttribute('role', 'listbox');
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', this.#onListboxKeyDown, {
      signal
    });
    this.addEventListener('blur', this.#onListboxBlur, {
      signal
    });
    this.#$listbox.addEventListener('click', this.#onListboxClick, {
      signal
    });
    subscribeContext(this, 'keydown', this.#onContextKeyDown, signal);
    subscribeContext(this, 'visibility', this.#onContextVisibility, signal);
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['rows'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'rows':
        {
          this.#$listbox.style.maxHeight = attrValueToPixels(newVal, {
            min: 1,
            itemSizeMultiplier: ITEM_HEIGHT
          });
          break;
        }
    }
  }
  set rows(value) {
    updateIntegerAttribute(this, 'rows', value);
  }
  get rows() {
    return getIntegerAttribute(this, 'rows', null);
  }
  #onListboxBlur = () => {
    this.#selectOption(null);
  };
  #onListboxClick = e => {
    this.focus();
    if (isSinchActionMenuOption(e.target)) {
      this.#selectOption(e.target);
    }
  };
  #onListboxKeyDown = e => {
    this.#handleKeydown(e);
  };
  #onContextKeyDown = e => {
    this.#handleKeydown(e.detail);
  };
  #onContextVisibility = e => {
    if (!e.detail) {
      this.#selectOption(null);
      this.#$listbox.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  };
  #handleKeydown(e) {
    switch (e.code) {
      case 'Enter':
      case 'Space':
        {
          e.preventDefault();
          const $opt = this.#findSelectedOption();
          if ($opt !== null) {
            $opt.click();
          }
          break;
        }
      case 'ArrowUp':
        {
          e.preventDefault();
          this.#selectOption(this.#getPrevOption());
          break;
        }
      case 'ArrowDown':
        {
          e.preventDefault();
          this.#selectOption(this.#getNextOption());
          break;
        }
    }
  }
  #getFirstOption() {
    const $options = this.#getOptionElements();
    for (let i = 0; i < $options.length; i++) {
      const el = $options[i];
      if (!getBooleanAttribute(el, 'disabled')) {
        return el;
      }
    }
    return null;
  }
  #getLastOption() {
    const $options = this.#getOptionElements();
    for (let i = $options.length - 1; i >= 0; i--) {
      const el = $options[i];
      if (!getBooleanAttribute(el, 'disabled')) {
        return el;
      }
    }
    return null;
  }
  #getNextOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      for (let i = 1; i <= $options.length; i++) {
        const el = $options[(i + index) % $options.length];
        if (!getBooleanAttribute(el, 'disabled')) {
          return el;
        }
      }
    }
    return this.#getFirstOption();
  }
  #getPrevOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      for (let i = 1; i <= $options.length; i++) {
        const el = $options[(index - i + $options.length) % $options.length];
        if (!getBooleanAttribute(el, 'disabled')) {
          return el;
        }
      }
    }
    return this.#getLastOption();
  }
  #selectOption($option) {
    const hasRows = this.hasAttribute('rows');
    for (const $op of this.#getOptionElements()) {
      const isSelected = $op === $option;
      updateBooleanAttribute($op, 'data-selected', isSelected);
      if (isSelected && hasRows) {
        $op.scrollIntoView?.({
          block: 'nearest'
        });
      }
    }
  }
  #getOptionElements() {
    return this.#$optionSlot.assignedElements();
  }
  #getSelectedOptionIndex() {
    const elements = this.#getOptionElements();
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (!getBooleanAttribute(el, 'disabled') && getBooleanAttribute(el, 'data-selected')) {
        return i;
      }
    }
    return null;
  }
  #findSelectedOption() {
    const elements = this.#getOptionElements();
    for (const el of elements) {
      if (getBooleanAttribute(el, 'data-selected')) {
        return el;
      }
    }
    return null;
  }
  get focusable() {
    return true;
  }
});